const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n: number) => String(n).padStart(2, '0');

export function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'local time';
  }
}

/** Absolute, in the viewer's zone. Relative age alone cannot be compared against a deploy log. */
export function formatLocal(iso: string, timeZone: string = viewerTimeZone()): string {
  const date = new Date(iso);
  // formatToParts throws RangeError on an Invalid Date, which would take the whole table down over
  // one bad timestamp. A dash reads as "no value" where the other cells already use one.
  if (Number.isNaN(date.getTime())) return '—';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone, day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  // ICU's 'short' month abbreviation for September varies by CLDR version ('Sep' vs 'Sept'),
  // so a laptop and CI can render the same instant differently. A numeric month plus our own
  // table is stable across Node/ICU versions.
  const month = MONTHS[Number(get('month')) - 1] ?? '';
  return `${get('day')} ${month}, ${pad(Number(get('hour')))}:${get('minute')}`;
}

function span(ms: number): string {
  const m = Math.round(Math.abs(ms) / 60000);
  if (m < 90) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${m % 60}m`;
  return `${Math.round(h / 24)}d`;
}

export function formatAgo(iso: string, nowMs: number): string {
  return `${span(nowMs - Date.parse(iso))} ago`;
}

export function formatLeft(iso: string, nowMs: number): string {
  return `${span(Date.parse(iso) - nowMs)} left`;
}

/** A plain span with no "ago"/"left" suffix — for a duration, not a deadline (e.g. how long a hold lasted). */
export function formatDuration(seconds: number): string {
  return span(seconds * 1000);
}
